import React, { useEffect, useMemo, useRef, useState } from "react";

/* ---------- utils ---------- */
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const within = (v, a, b) => v >= Math.min(a, b) && v <= Math.max(a, b);

function rectSizeNorm(iw, ih, aspect, z) {
  const imageRatio = iw / ih;
  const aspectPrime = aspect / imageRatio;
  let wNorm, hNorm;
  if (aspectPrime >= 1) {
    wNorm = 1;
    hNorm = 1 / aspectPrime;
  } else {
    wNorm = aspectPrime;
    hNorm = 1;
  }
  wNorm /= z;
  hNorm /= z;
  return { wNorm, hNorm };
}

function circleRadiusNorm(z) {
  return 1 / (2 * z);
}

function clampCenterForPreset(cx, cy, iw, ih, preset, zoom) {
  if (!preset) return { cx: clamp(cx, 0, 1), cy: clamp(cy, 0, 1) };
  if (preset.shape === "rect") {
    const { wNorm, hNorm } = rectSizeNorm(iw, ih, preset.aspect, zoom || 1);
    const halfW = wNorm / 2, halfH = hNorm / 2;
    return {
      cx: clamp(cx, halfW, 1 - halfW),
      cy: clamp(cy, halfH, 1 - halfH),
    };
  } else {
    const r = circleRadiusNorm(zoom || 1);
    const rx = r * Math.min(iw, ih) / iw;
    const ry = r * Math.min(iw, ih) / ih;
    return {
      cx: clamp(cx, rx, 1 - rx),
      cy: clamp(cy, ry, 1 - ry),
    };
  }
}

function rectBox(iw, ih, cx, cy, z, aspect) {
  const { wNorm, hNorm } = rectSizeNorm(iw, ih, aspect, z);
  const halfW = wNorm / 2, halfH = hNorm / 2;
  const c2x = clamp(cx, halfW, 1 - halfW);
  const c2y = clamp(cy, halfH, 1 - halfH);
  return {
    x: c2x - halfW,
    y: c2y - halfH,
    width: wNorm,
    height: hNorm,
    center: { x: c2x, y: c2y },
  };
}

function buildCropsJSON(iw, ih, centersMap, presets, zoomMap, fallbackCenter) {
  const crops = {};
  presets.forEach(p => {
    const z = zoomMap[p.key] || 1;
    const cen = centersMap[p.key] || fallbackCenter || { x: 0.5, y: 0.5 };
    if (p.shape === "rect") {
      const { x, y, width, height, center } = rectBox(iw, ih, cen.x, cen.y, z, p.aspect);
      crops[p.key] = { shape: "rect", aspect: p.aspect, box: { x, y, width, height }, center };
    } else {
      const r = circleRadiusNorm(z);
      const rx = r * Math.min(iw, ih) / iw;
      const ry = r * Math.min(iw, ih) / ih;
      const c2x = clamp(cen.x, rx, 1 - rx);
      const c2y = clamp(cen.y, ry, 1 - ry);
      crops[p.key] = { shape: "circle", center: { x: c2x, y: c2y }, radius: r };
    }
  });
  return crops;
}

function CropPreview({ iw, ih, crop, src, label, shape = "rect", size = 96 }) {
  if (!iw || !ih || !crop) return null;

  let box;
  if (shape === "rect") {
    box = crop.box;
  } else {
    const r = crop.radius;
    const sidePx = 2 * r * Math.min(iw, ih);
    const cxPx = crop.center.x * iw;
    const cyPx = crop.center.y * ih;
    box = {
      x: (cxPx - sidePx / 2) / iw,
      y: (cyPx - sidePx / 2) / ih,
      width: sidePx / iw,
      height: sidePx / ih,
    };
  }

  const cropWpx = box.width * iw;
  const cropHpx = box.height * ih;
  const aspect = cropWpx / cropHpx;
  const w = size;
  const h = Math.max(1, Math.round(size / aspect));

  const scale = w / cropWpx;
  const imgW = iw * scale;
  const imgH = ih * scale;
  const offsetX = -(box.x * iw) * scale;
  const offsetY = -(box.y * ih) * scale;

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
      <div style={{
        width: w, height: h, borderRadius: shape === "circle" ? "50%" : "10px",
        overflow: "hidden", border: "1px solid rgba(0,0,0,0.1)", position: "relative", background: "#f7f7f7",
      }}>
        <img alt="" src={src} style={{ width: imgW, height: imgH, transform: `translate(${offsetX}px, ${offsetY}px)`, userSelect: "none", pointerEvents: "none", display: "block" }} />
      </div>
      <div style={{ fontSize: 12, color: "#444", marginTop: 6 }}>{label}</div>
    </div>
  );
}

/* letterboxing helper */
function getDisplayedImageBox(stageEl, iw, ih) {
  const r = stageEl.getBoundingClientRect();
  const stageW = r.width, stageH = r.height;
  const imgRatio = iw / ih;
  const stageRatio = stageW / stageH;
  let displayW, displayH, leftPad = 0, topPad = 0;
  if (imgRatio > stageRatio) {
    displayW = stageW;
    displayH = stageW / imgRatio;
    topPad = (stageH - displayH) / 2;
  } else {
    displayH = stageH;
    displayW = stageH * imgRatio;
    leftPad = (stageW - displayW) / 2;
  }
  return { stageW, stageH, displayW, displayH, leftPad, topPad };
}

export default function ImageCropModal({
  mode = "people",               // "people" | "newsRect"
  file,
  onCancel,
  onConfirm,
  presets = [],
  allowCustom = false,
  initialZoom = 1.2,
  initialFocal = { x: 0.5, y: 0.5 },
}) {
  const [src, setSrc] = useState("");
  const [iw, setIw] = useState(0);
  const [ih, setIh] = useState(0);

  /* -------- NEWS (focus + article crop) -------- */
  const [newsTab, setNewsTab] = useState("focus"); // "focus" | "crop"

  // focus point
  const [cx, setCx] = useState(initialFocal.x);
  const [cy, setCy] = useState(initialFocal.y);

  // article crop (freeform)
  const [cropEnabled, setCropEnabled] = useState(false);
  const [cropCx, setCropCx] = useState(0.5);
  const [cropCy, setCropCy] = useState(0.5);
  const [cropW, setCropW] = useState(0.6); // normalized width
  const [cropH, setCropH] = useState(0.6); // normalized height

  /* -------- PEOPLE (unchanged) -------- */
  const [activeKey, setActiveKey] = useState(presets[0]?.key || "");
  const [extraPresets, setExtraPresets] = useState([]);
  const allPresets = useMemo(() => [...presets, ...extraPresets], [presets, extraPresets]);

  const [zoomMap, setZoomMap] = useState(() =>
    Object.fromEntries(presets.map(p => [p.key, initialZoom]))
  );
  const [centerMap, setCenterMap] = useState(() =>
    Object.fromEntries(presets.map(p => [p.key, { x: initialFocal.x, y: initialFocal.y }]))
  );

  const stageRef = useRef(null);

  /* ensure defaults for newly added presets (people) */
  useEffect(() => {
    setZoomMap(zm => {
      const next = { ...zm };
      [...presets, ...extraPresets].forEach(p => {
        if (next[p.key] == null) next[p.key] = initialZoom;
      });
      return next;
    });
    setCenterMap(cm => {
      const next = { ...cm };
      [...presets, ...extraPresets].forEach(p => {
        if (next[p.key] == null) next[p.key] = { x: initialFocal.x, y: initialFocal.y };
      });
      return next;
    });
    if (mode === "people" && !activeKey && (presets[0]?.key || extraPresets[0]?.key)) {
      setActiveKey(presets[0]?.key || extraPresets[0]?.key);
    }
  }, [presets, extraPresets, initialZoom, initialFocal, mode]); // eslint-disable-line

  /* load image */
  useEffect(() => {
    let urlPromise;
    if (file instanceof File || file instanceof Blob) {
      urlPromise = new Promise(res => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.readAsDataURL(file);
      });
    } else {
      urlPromise = Promise.resolve(file);
    }
    urlPromise.then(u => {
      setSrc(u);
      const img = new Image();
      img.onload = () => { setIw(img.naturalWidth); setIh(img.naturalHeight); };
      img.src = u;
    });
  }, [file]);

  /* active preset + clamps (people) */
  const activePreset = mode === "people" ? (allPresets.find(p => p.key === activeKey) || allPresets[0]) : null;
  const activeZoom = mode === "people" ? (zoomMap[activePreset?.key] || initialZoom) : 1;
  const activeCenter = mode === "people"
    ? (centerMap[activePreset?.key] || { x: initialFocal.x, y: initialFocal.y })
    : { x: cx, y: cy };

  useEffect(() => {
    if (mode !== "people" || !iw || !ih) return;
    setCenterMap(cm => {
      let changed = false;
      const next = { ...cm };
      allPresets.forEach(p => {
        const c = next[p.key] || { x: initialFocal.x, y: initialFocal.y };
        const z = zoomMap[p.key] || 1;
        const clamped = clampCenterForPreset(c.x, c.y, iw, ih, p, z);
        if (clamped.cx !== c.x || clamped.cy !== c.y) {
          next[p.key] = { x: clamped.cx, y: clamped.cy };
          changed = true;
        }
      });
      return changed ? next : cm;
    });
  }, [zoomMap, iw, ih, allPresets, mode, initialFocal]); // eslint-disable-line

  /* stage mapping */
  const imgToNormFromEvent = (e) => {
    if (!stageRef.current || !iw || !ih) return null;
    const rect = stageRef.current.getBoundingClientRect();
    const stageRatio = rect.width / rect.height;
    const imgRatio = iw / ih;
    let imgX, imgY;

    if (imgRatio > stageRatio) {
      const displayW = rect.width;
      const displayH = rect.width / imgRatio;
      const topPad = (rect.height - displayH) / 2;
      const yWithin = clamp((e.clientY - rect.top - topPad) / displayH, 0, 1);
      const xWithin = clamp((e.clientX - rect.left) / displayW, 0, 1);
      imgX = xWithin; imgY = yWithin;
    } else {
      const displayH = rect.height;
      const displayW = rect.height * imgRatio;
      const leftPad = (rect.width - displayW) / 2;
      const xWithin = clamp((e.clientX - rect.left - leftPad) / displayW, 0, 1);
      const yWithin = clamp((e.clientY - rect.top) / displayH, 0, 1);
      imgX = xWithin; imgY = yWithin;
    }
    return { x: imgX, y: imgY };
  };

  /* ------- NEWS: crop clamps & drag ------- */
  const minNorm = useMemo(() => ({
    w: iw ? Math.min(1, 30 / iw) : 0.01,
    h: ih ? Math.min(1, 30 / ih) : 0.01,
  }), [iw, ih]);

  const clampCropCenter = (ccx, ccy, w, h) => {
    const halfW = w / 2, halfH = h / 2;
    return {
      x: clamp(ccx, halfW, 1 - halfW),
      y: clamp(ccy, halfH, 1 - halfH),
    };
  };

  const [draggingCrop, setDraggingCrop] = useState(false);

  /* wheel for people zoom only */
  const onWheel = (e) => {
    if (mode !== "people") return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.08 : -0.08;
    setZoomMap(zm => {
      const z = clamp((zm[activeKey] || initialZoom) + delta, 1, 5);
      return { ...zm, [activeKey]: z };
    });
  };

  /* build people crops for previews */
  const crops = useMemo(() => {
    if (!iw || !ih) return {};
    if (mode !== "people" || !allPresets.length) return {};
    return buildCropsJSON(iw, ih, centerMap, allPresets, zoomMap, { x: initialFocal.x, y: initialFocal.y });
  }, [iw, ih, centerMap, allPresets, zoomMap, mode, initialFocal]);

  /* overlay (people) */
  const peopleOverlay = useMemo(() => {
    if (mode !== "people") return null;
    if (!iw || !ih || !activePreset) return null;
    const c = crops[activePreset.key];
    if (!c) return null;
    if (activePreset.shape === "rect") {
      return { type: "rect", ...c.box };
    } else {
      return { type: "circle", cx: c.center.x, cy: c.center.y, r: c.radius };
    }
  }, [iw, ih, activePreset, crops, mode]);

  /* overlay (news crop) — compute rect box from center + size */
  const newsRect = useMemo(() => {
    if (!cropEnabled) return null;
    const w = clamp(cropW, minNorm.w, 1);
    const h = clamp(cropH, minNorm.h, 1);
    const c = clampCropCenter(cropCx, cropCy, w, h);
    return {
      x: c.x - w / 2,
      y: c.y - h / 2,
      width: w,
      height: h,
      center: c,
    };
  }, [cropEnabled, cropCx, cropCy, cropW, cropH, minNorm]);

  const stageStyle = {
    position: "relative",
    width: "100%",
    height: 420,
    background: "#111",
    borderRadius: 12,
    overflow: "hidden",
    userSelect: "none",
    touchAction: "none",
    cursor: mode === "newsRect" && newsTab === "crop" && cropEnabled ? (draggingCrop ? "grabbing" : "grab") : "default",
  };

  const imgInStage = {
    style: {
      position: "absolute",
      inset: 0,
      margin: "auto",
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain",
      display: "block",
      pointerEvents: "none",
    },
  };

  /* overlays rendering */
  const overlayNode = (() => {
    if (!stageRef.current || !iw || !ih) return null;

    const shade = "rgba(0,0,0,0.55)";
    const { stageW, stageH, displayW, displayH, leftPad, topPad } =
      getDisplayedImageBox(stageRef.current, iw, ih);

    // people overlays
    if (mode === "people" && peopleOverlay) {
      if (peopleOverlay.type === "rect") {
        const xPx = leftPad + peopleOverlay.x * displayW;
        const yPx = topPad + peopleOverlay.y * displayH;
        const wPx = peopleOverlay.width * displayW;
        const hPx = peopleOverlay.height * displayH;

        return (
          <>
            <div style={{ position: "absolute", left: 0, top: 0, width: stageW, height: yPx, background: shade, pointerEvents: "none" }} />
            <div style={{ position: "absolute", left: 0, top: yPx, width: xPx, height: hPx, background: shade, pointerEvents: "none" }} />
            <div style={{ position: "absolute", left: xPx + wPx, top: yPx, width: stageW - (xPx + wPx), height: hPx, background: shade, pointerEvents: "none" }} />
            <div style={{ position: "absolute", left: 0, top: yPx + hPx, width: stageW, height: stageH - (yPx + hPx), background: shade, pointerEvents: "none" }} />
            <div
              style={{
                position: "absolute",
                left: xPx, top: yPx, width: wPx, height: hPx,
                border: "2px solid rgba(255,255,255,0.9)", borderRadius: 10, pointerEvents: "none",
                boxShadow: "0 0 0 1px rgba(0,0,0,0.6) inset",
              }}
            />
          </>
        );
      } else {
        const cxPx = leftPad + peopleOverlay.cx * displayW;
        const cyPx = topPad + peopleOverlay.cy * displayH;
        const rPx = peopleOverlay.r * Math.min(displayW, displayH);

        return (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: shade,
              pointerEvents: "none",
              clipPath: `circle(${rPx}px at ${cxPx}px ${cyPx}px)`,
            }}
          />
        );
      }
    }

    // news crop overlay
    if (mode === "newsRect" && newsTab === "crop" && newsRect) {
      const xPx = leftPad + newsRect.x * displayW;
      const yPx = topPad + newsRect.y * displayH;
      const wPx = newsRect.width * displayW;
      const hPx = newsRect.height * displayH;

      return (
        <>
          <div style={{ position: "absolute", left: 0, top: 0, width: stageW, height: yPx, background: shade }} />
          <div style={{ position: "absolute", left: 0, top: yPx, width: xPx, height: hPx, background: shade }} />
          <div style={{ position: "absolute", left: xPx + wPx, top: yPx, width: stageW - (xPx + wPx), height: hPx, background: shade }} />
          <div style={{ position: "absolute", left: 0, top: yPx + hPx, width: stageW, height: stageH - (yPx + hPx), background: shade }} />
          <div
            style={{
              position: "absolute",
              left: xPx, top: yPx, width: wPx, height: hPx,
              border: "2px solid #0b5", borderRadius: 8, boxShadow: "0 0 0 1px rgba(0,0,0,0.4) inset",
            }}
          />
        </>
      );
    }

    return null;
  })();

  /* marker (shared for focus point) */
  const markerPos = (() => {
    if (!stageRef.current) return null;
    const c = activeCenter ?? { x: cx, y: cy };
    const { displayW, displayH, leftPad, topPad } =
      getDisplayedImageBox(stageRef.current, iw, ih);
    return {
      left: leftPad + (c.x * displayW),
      top:  topPad + (c.y * displayH),
    };
  })();

  /* add custom aspect preset (unchanged) */
  const addCustomAspect = (e) => {
    e.preventDefault();
    const form = e.target;
    const w = Number(form.w.value);
    const h = Number(form.h.value);
    const label = (form.label.value || `${w}:${h}`).trim();
    if (!w || !h) return;
    const aspect = w / h;
    const key = `news_${w}x${h}`;
    setExtraPresets(prev => [...prev, { key, label, shape: "rect", aspect }]);
    setZoomMap(zm => ({ ...zm, [key]: zoomMap[activeKey] || initialZoom }));
    setCenterMap(cm => ({ ...cm, [key]: { ...(centerMap[activeKey] || { x: initialFocal.x, y: initialFocal.y }) } }));
    setActiveKey(key);
    form.reset();
  };

  /* confirm payload */
  const confirm = () => {
    if (!iw || !ih) return;

    if (mode === "people") {
      const cropJson = {
        version: 3,
        mode: "people",
        original: { width: iw, height: ih },
        focalPoint: { x: activeCenter.x, y: activeCenter.y },
        focalPoints: centerMap,
        zooms: zoomMap,
        crops,
      };
      onConfirm?.(cropJson);
    } else {
      const payload = {
        version: 1,
        mode: "focuspoint",
        original: { width: iw, height: ih },
        focalPoint: { x: cx, y: cy },
      };
      if (cropEnabled && newsRect) {
        payload.articleCrop = {
          enabled: true,
          aspect: null,
          box: { x: newsRect.x, y: newsRect.y, width: newsRect.width, height: newsRect.height },
        };
      } else {
        payload.articleCrop = { enabled: false };
      }
      onConfirm?.(payload);
    }
  };

  if (!src) return null;

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel?.();
        if (e.key === "Enter") confirm();
        const step = 0.005;
        if (["ArrowLeft","ArrowRight","ArrowUp","ArrowDown"].includes(e.key)) {
          e.preventDefault();
          if (mode === "people" && activePreset) {
            let nx = activeCenter.x, ny = activeCenter.y;
            if (e.key === "ArrowLeft") nx -= step;
            if (e.key === "ArrowRight") nx += step;
            if (e.key === "ArrowUp") ny -= step;
            if (e.key === "ArrowDown") ny += step;
            const z = zoomMap[activePreset.key] || 1;
            const cl = clampCenterForPreset(nx, ny, iw, ih, activePreset, z);
            setCenterMap(cm => ({ ...cm, [activePreset.key]: { x: cl.cx, y: cl.cy } }));
          } else if (mode === "newsRect" && newsTab === "focus") {
            let nx = cx, ny = cy;
            if (e.key === "ArrowLeft") nx -= step;
            if (e.key === "ArrowRight") nx += step;
            if (e.key === "ArrowUp") ny -= step;
            if (e.key === "ArrowDown") ny += step;
            setCx(clamp(nx, 0, 1));
            setCy(clamp(ny, 0, 1));
          } else if (mode === "newsRect" && newsTab === "crop" && cropEnabled) {
            let nx = cropCx, ny = cropCy;
            if (e.key === "ArrowLeft") nx -= step;
            if (e.key === "ArrowRight") nx += step;
            if (e.key === "ArrowUp") ny -= step;
            if (e.key === "ArrowDown") ny += step;
            const w = clamp(cropW, minNorm.w, 1);
            const h = clamp(cropH, minNorm.h, 1);
            const c = clampCropCenter(nx, ny, w, h);
            setCropCx(c.x);
            setCropCy(c.y);
          }
        }
      }}
      tabIndex={-1}
    >
        <div
          style={{
            background: "#fff",
            width: 920,
            maxWidth: "100%",
            maxHeight: "calc(100vh - 32px)", // keep it on screen
            overflow: "auto",                 // allow scrolling inside
            overscrollBehavior: "contain",    // stop scrolling the page behind
            borderRadius: 16,
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
          }}
        >
        <header style={{ padding: "14px 18px", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", gap: 12 }}>
          <strong style={{ fontSize: 16 }}>
            {mode === "people" ? "Adjust crop" : "Focus point & Article crop"}
          </strong>
          <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
            <button onClick={onCancel} style={{ padding: "10px 16px", fontSize: 15, borderRadius: 10 }}>Cancel</button>
            <button onClick={confirm} style={{ padding: "10px 18px", fontSize: 15, borderRadius: 10, fontWeight: 700, background: "#0b5", color: "#fff", border: "none" }}>
              Confirm
            </button>
          </div>
        </header>

        <div style={{ padding: 16 }}>
          {/* PEOPLE tabs */}
          {mode === "people" && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              {allPresets.map(p => (
                <button
                  key={p.key}
                  onClick={() => setActiveKey(p.key)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 999,
                    border: "1px solid #ddd",
                    background: p.key === activeKey ? "#0b5" : "#fff",
                    color: p.key === activeKey ? "#fff" : "#333",
                    fontSize: 13
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}

          {/* NEWS tabs */}
          {mode === "newsRect" && (
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {["focus", "crop"].map(t => (
                <button
                  key={t}
                  onClick={() => setNewsTab(t)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 999,
                    border: "1px solid #ddd",
                    background: t === newsTab ? "#0b5" : "#fff",
                    color: t === newsTab ? "#fff" : "#333",
                    fontSize: 13,
                  }}
                >
                  {t === "focus" ? "Focus point" : "Article crop"}
                </button>
              ))}
            </div>
          )}

          {/* Stage */}
          <div
            ref={stageRef}
            style={stageStyle}
            onMouseDown={(e) => {
              if (mode === "newsRect" && newsTab === "crop" && cropEnabled) {
                // drag the crop window by its center
                const pt = imgToNormFromEvent(e);
                if (!pt) return;
                setDraggingCrop(true);

                const start = { x: pt.x, y: pt.y, ccx: cropCx, ccy: cropCy };
                const move = (ev) => {
                  const p = imgToNormFromEvent(ev);
                  if (!p) return;
                  const dx = p.x - start.x;
                  const dy = p.y - start.y;
                  const w = clamp(cropW, minNorm.w, 1);
                  const h = clamp(cropH, minNorm.h, 1);
                  const c = clampCropCenter(start.ccx + dx, start.ccy + dy, w, h);
                  setCropCx(c.x);
                  setCropCy(c.y);
                };
                const up = () => {
                  setDraggingCrop(false);
                  window.removeEventListener("mousemove", move);
                  window.removeEventListener("mouseup", up);
                };
                window.addEventListener("mousemove", move);
                window.addEventListener("mouseup", up);
              } else {
                // focus/people: move focal by dragging
                const setFocalFromEvent = (ev) => {
                  const pt = imgToNormFromEvent(ev);
                  if (!pt) return;
                  if (mode === "people") {
                    if (!activePreset) return;
                    const z = zoomMap[activePreset.key] || 1;
                    const cl = clampCenterForPreset(pt.x, pt.y, iw, ih, activePreset, z);
                    setCenterMap(cm => ({ ...cm, [activePreset.key]: { x: cl.cx, y: cl.cy } }));
                  } else {
                    setCx(pt.x);
                    setCy(pt.y);
                  }
                };
                setFocalFromEvent(e);
                const move = (ev) => setFocalFromEvent(ev);
                const up = () => {
                  window.removeEventListener("mousemove", move);
                  window.removeEventListener("mouseup", up);
                };
                window.addEventListener("mousemove", move);
                window.addEventListener("mouseup", up);
              }
            }}
            onWheel={onWheel}
          >
            <img alt="" src={src} style={imgInStage.style} />
            {/* Focus marker (on both modes, visible on focus tab or people mode) */}
            {(mode === "people" || newsTab === "focus") && markerPos && (
              <div style={{
                position: "absolute",
                left: markerPos.left,
                top: markerPos.top,
                transform: "translate(-50%, -50%)",
                width: 12, height: 12, borderRadius: "50%",
                background: "#0b5", boxShadow: "0 0 0 2px rgba(255,255,255,0.9)",
                pointerEvents: "none",
              }} />
            )}
            {overlayNode}
          </div>

          {/* PEOPLE controls */}
          {mode === "people" && activePreset && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                <label style={{ fontSize: 13, color: "#444", minWidth: 70 }}>Zoom ({activePreset?.label})</label>
                <input
                  type="range" min={1} max={5} step={0.01}
                  value={activeZoom}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setZoomMap(zm => ({ ...zm, [activeKey]: v }));
                  }}
                  style={{ flex: 1 }}
                />
                <div style={{ width: 64, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{activeZoom.toFixed(2)}×</div>
              </div>

              {allowCustom && (
                <form onSubmit={addCustomAspect} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
                  <span style={{ fontSize: 13, color: "#444" }}>Add aspect:</span>
                  <input name="w" type="number" min="1" placeholder="W" style={{ width: 70 }} />
                  <input name="h" type="number" min="1" placeholder="H" style={{ width: 70 }} />
                  <input name="label" type="text" placeholder="Label (optional)" style={{ width: 160 }} />
                  <button type="submit">Add</button>
                </form>
              )}

              <div style={{ marginTop: 14 }}>
                {allPresets.map(p => (
                  <CropPreview
                    key={p.key}
                    iw={iw}
                    ih={ih}
                    crop={crops[p.key]}
                    src={src}
                    label={p.label}
                    shape={p.shape}
                    size={110}
                  />
                ))}
              </div>
            </>
          )}

          {/* NEWS controls */}
          {mode === "newsRect" && newsTab === "focus" && (
            <div style={{ marginTop: 12, fontSize: 13, color: "#555" }}>
              Click or drag to place the focus point. This point is used for banners and other responsive placements.
            </div>
          )}

          {mode === "newsRect" && newsTab === "crop" && (
            <div style={{ marginTop: 12 }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={cropEnabled}
                  onChange={(e) => setCropEnabled(e.target.checked)}
                />
                Enable article crop (freeform)
              </label>

              <div style={{ opacity: cropEnabled ? 1 : 0.5, pointerEvents: cropEnabled ? "auto" : "none", transition: "opacity .15s", marginTop: 10 }}>
                {/* Center X */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                  <label style={{ minWidth: 90, fontSize: 13, color: "#444" }}>Center X</label>
                  <input
                    type="range" min={0} max={1} step={0.001}
                    value={cropCx}
                    onChange={(e) => {
                      const w = clamp(cropW, minNorm.w, 1), h = clamp(cropH, minNorm.h, 1);
                      const c = clampCropCenter(parseFloat(e.target.value), cropCy, w, h);
                      setCropCx(c.x);
                      setCropCy(c.y);
                    }}
                    style={{ flex: 1 }}
                  />
                  <div style={{ width: 56, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{cropCx.toFixed(3)}</div>
                </div>
                {/* Center Y */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
                  <label style={{ minWidth: 90, fontSize: 13, color: "#444" }}>Center Y</label>
                  <input
                    type="range" min={0} max={1} step={0.001}
                    value={cropCy}
                    onChange={(e) => {
                      const w = clamp(cropW, minNorm.w, 1), h = clamp(cropH, minNorm.h, 1);
                      const c = clampCropCenter(cropCx, parseFloat(e.target.value), w, h);
                      setCropCx(c.x);
                      setCropCy(c.y);
                    }}
                    style={{ flex: 1 }}
                  />
                  <div style={{ width: 56, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{cropCy.toFixed(3)}</div>
                </div>
                {/* Width */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
                  <label style={{ minWidth: 90, fontSize: 13, color: "#444" }}>Width</label>
                  <input
                    type="range" min={minNorm.w} max={1} step={0.001}
                    value={cropW}
                    onChange={(e) => {
                      const w = clamp(parseFloat(e.target.value), minNorm.w, 1);
                      const c = clampCropCenter(cropCx, cropCy, w, cropH);
                      setCropW(w);
                      setCropCx(c.x); setCropCy(c.y);
                    }}
                    style={{ flex: 1 }}
                  />
                  <div style={{ width: 56, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{cropW.toFixed(3)}</div>
                </div>
                {/* Height */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
                  <label style={{ minWidth: 90, fontSize: 13, color: "#444" }}>Height</label>
                  <input
                    type="range" min={minNorm.h} max={1} step={0.001}
                    value={cropH}
                    onChange={(e) => {
                      const h = clamp(parseFloat(e.target.value), minNorm.h, 1);
                      const c = clampCropCenter(cropCx, cropCy, cropW, h);
                      setCropH(h);
                      setCropCx(c.x); setCropCy(c.y);
                    }}
                    style={{ flex: 1 }}
                  />
                  <div style={{ width: 56, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{cropH.toFixed(3)}</div>
                </div>

                {/* live mini preview */}
                <div style={{ marginTop: 12 }}>
                  {newsRect && (
                    <CropPreview
                      iw={iw}
                      ih={ih}
                      crop={{ box: { x: newsRect.x, y: newsRect.y, width: newsRect.width, height: newsRect.height } }}
                      src={src}
                      label="Article crop"
                      shape="rect"
                      size={160}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
